/**
 * SENTINEL - C2 Edge Intelligence Platform - Main Application
 *
 * 9-tab terminal-style interface for operational intelligence.
 * Keyboard-first UX with [1]-[9] tab switching.
 */

import { useState, useEffect, useCallback } from 'react'
import type { TabId, Domain, EdgeStatus } from './types'
import { TABS } from './types'
import { ErrorBoundary } from './components/ErrorBoundary'
import { getEdgeStatus, toggleDDIL, triggerSync } from './services/api'

// Layout
import { Header } from './components/layout/Header'
import { TabNav } from './components/layout/TabNav'
import { StatusBar } from './components/layout/StatusBar'
import { EdgeBanner } from './components/layout/EdgeBanner'
import { TheaterSetupModal } from './components/shared/TheaterSetupModal'

// Radio Chatter Monitor
import { RadioOverlay } from './components/radio/RadioOverlay'
import { useRadioAvailable } from './hooks/useRadioAvailable'

// Panels
import { LogisticsPanel } from './components/panels/logistics/LogisticsPanel'
import { ForceCompositionPanel } from './components/panels/force/ForceCompositionPanel'
import { TempoAlertsPanel } from './components/panels/tempo/TempoAlertsPanel'
import { AssetReadinessPanel } from './components/panels/readiness/AssetReadinessPanel'
import { FusionMapPanel } from './components/panels/fusion/FusionMapPanel'
import { CrossDomainPanel } from './components/panels/crossdomain/CrossDomainPanel'
import { IntelAssessmentPanel } from './components/panels/intel/IntelAssessmentPanel'
import { AuditTrailPanel } from './components/panels/audit/AuditTrailPanel'
import { AAREnginePanel } from './components/panels/aar/AAREnginePanel'
import { TacticalPicturePanel } from './components/panels/tactical/TacticalPicturePanel'

export function App() {
  const [activeTab, setActiveTab] = useState<TabId>('force')
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null)
  const [activeDomainFilter, setActiveDomainFilter] = useState<Domain | null>(null)
  const [edgeStatus, setEdgeStatus] = useState<EdgeStatus | null>(null)
  const [showTheaterModal, setShowTheaterModal] = useState(false)
  const [theaterVersion, setTheaterVersion] = useState(0)
  const radioAvailable = useRadioAvailable()
  const [radioVisible, setRadioVisible] = useState<boolean>(() => {
    try { return localStorage.getItem('sentinel-radio-visible') === 'true' } catch { return false }
  })

  // Persist radio visibility
  useEffect(() => {
    try { localStorage.setItem('sentinel-radio-visible', String(radioVisible)) } catch { /* ignore */ }
  }, [radioVisible])

  // Close radio panel if server becomes unavailable
  useEffect(() => {
    if (!radioAvailable && radioVisible) {
      setRadioVisible(false)
    }
  }, [radioAvailable, radioVisible])

  // Fetch edge status periodically
  const fetchEdgeStatus = useCallback(async () => {
    const res = await getEdgeStatus()
    if (res.data) setEdgeStatus(res.data)
  }, [])

  useEffect(() => {
    fetchEdgeStatus()
    const interval = setInterval(fetchEdgeStatus, 10000)
    return () => clearInterval(interval)
  }, [fetchEdgeStatus])

  // Handle DDIL toggle
  const handleDDILToggle = useCallback(async (level: number) => {
    const res = await toggleDDIL(level)
    if (res.data) {
      fetchEdgeStatus()
    }
  }, [fetchEdgeStatus])

  // Handle sync
  const handleSync = useCallback(async () => {
    await triggerSync()
    fetchEdgeStatus()
  }, [fetchEdgeStatus])

  // Keyboard shortcuts: [1]-[9] for tabs, [Esc] to clear
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT'

      if (e.key === 'Escape') {
        ;(document.activeElement as HTMLElement)?.blur()
        return
      }

      if (isInput) return

      // [0]-[9] for tab switching
      if (e.key >= '0' && e.key <= '9') {
        const tab = TABS.find(t => t.shortcut === e.key)
        if (tab) setActiveTab(tab.id)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Handle asset selection from panels
  const handleSelectAsset = useCallback((assetId: string) => {
    setSelectedAsset(assetId)
    setActiveTab('readiness') // Jump to readiness panel for detail
  }, [])

  // Handle tab change
  const handleTabChange = useCallback((tabId: TabId) => {
    setActiveTab(tabId)
  }, [])

  // Handle theater configuration changes
  const handleTheaterChange = useCallback(() => {
    setTheaterVersion(v => v + 1)
  }, [])

  // Render the active panel
  const renderPanel = () => {
    switch (activeTab) {
      case 'logistics':
        return <LogisticsPanel theaterVersion={theaterVersion} onTheaterChange={handleTheaterChange} />
      case 'force':
        return (
          <ForceCompositionPanel
            activeDomainFilter={activeDomainFilter}
            onSelectAsset={handleSelectAsset}
          />
        )
      case 'tempo':
        return <TempoAlertsPanel onSelectAsset={handleSelectAsset} />
      case 'readiness':
        return <AssetReadinessPanel selectedAsset={selectedAsset} />
      case 'fusion':
        return <FusionMapPanel />
      case 'crossdomain':
        return <CrossDomainPanel />
      case 'intel':
        return <IntelAssessmentPanel edgeStatus={edgeStatus} />
      case 'audit':
        return <AuditTrailPanel />
      case 'aar':
        return <AAREnginePanel />
      case 'tactical':
        return <TacticalPicturePanel edgeStatus={edgeStatus} />
      default:
        return (
          <div className="flex items-center justify-center h-full text-term-textDim">
            Unknown panel
          </div>
        )
    }
  }

  return (
    <div className="h-screen flex flex-col bg-term-bgDark">
      {/* Edge Banner (shows when not FULL connectivity) */}
      <EdgeBanner edgeStatus={edgeStatus} onSync={handleSync} />

      {/* Header: branding + domain filters + edge status + radio toggle */}
      <Header
        activeDomainFilter={activeDomainFilter}
        onDomainFilterChange={setActiveDomainFilter}
        selectedAsset={selectedAsset}
        edgeStatus={edgeStatus}
        onDDILToggle={handleDDILToggle}
        onTheaterSetup={() => setShowTheaterModal(true)}
        radioVisible={radioVisible}
        radioAvailable={radioAvailable}
        onRadioToggle={() => setRadioVisible((v) => !v)}
      />

      {/* Tab Navigation */}
      <TabNav activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Main Content + Radio Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 overflow-hidden p-4">
          <div className="h-full term-box p-4 overflow-auto">
            <ErrorBoundary key={activeTab}>
              {renderPanel()}
            </ErrorBoundary>
          </div>
        </main>

        {/* Radio Overlay — renders nothing when hidden */}
        {radioVisible && <RadioOverlay onClose={() => setRadioVisible(false)} />}
      </div>

      {/* Status Bar Footer */}
      <StatusBar edgeStatus={edgeStatus} />

      {/* Theater Setup Modal */}
      {showTheaterModal && (
        <TheaterSetupModal
          onClose={() => setShowTheaterModal(false)}
          onTheaterChange={handleTheaterChange}
        />
      )}
    </div>
  )
}
